import { eccLevel, isEcc, utf8ByteLength, type Ecc } from "./qr/capacity";
import { describeSymbol, friendlyError } from "./qr/encode";
import { clampSize, safeFileStem } from "./qr/filename";
import { attachPreview, exportBlob, previewImage } from "./qr/render";
import { canShareFiles, isTouchDevice, sharePng } from "./share";
import {
	COLOR_STYLES,
	DEFAULT_STYLE,
	LOGO_ECC,
	SHAPE_STYLES,
	effectiveEcc,
	parseStyle,
	type ColorStyle,
	type QrStyle,
	type ShapeStyle,
} from "./qr/style";

const DEFAULT_TEXT = "https://www.metrolinagreenhouses.com/";
/** Preview is drawn at this many device pixels and CSS-scaled down, so it stays crisp on HiDPI. */
const PREVIEW_PX = 640;
const SIZE = { min: 64, max: 4096, fallback: 512 };
const DEBOUNCE_MS = 120;
const STYLE_STORAGE_KEY = "qrcodecreator.style";

interface Refs {
	form: HTMLFormElement;
	data: HTMLTextAreaElement;
	byteCount: HTMLElement;
	byteLimit: HTMLElement;
	size: HTMLInputElement;
	preview: HTMLElement;
	status: HTMLElement;
	errorBox: HTMLElement;
	logoToggle: HTMLInputElement;
	logoHint: HTMLElement;
	share: HTMLButtonElement;
	downloadPng: HTMLButtonElement;
	downloadSvg: HTMLButtonElement;
	copyPng: HTMLButtonElement;
}

interface Rendered {
	text: string;
	ecc: Ecc;
	style: QrStyle;
}

export function initGenerator(root: HTMLElement): void {
	const q = <T extends HTMLElement>(selector: string): T => {
		const el = root.querySelector<T>(selector);
		if (!el) throw new Error(`Missing element ${selector}`);
		return el;
	};

	const refs: Refs = {
		form: q("#qrForm"),
		data: q("#dataInput"),
		byteCount: q("#byteCount"),
		byteLimit: q("#byteLimit"),
		size: q("#sizeInput"),
		preview: q("#preview"),
		status: q("#status"),
		errorBox: q("#errorBox"),
		logoToggle: q("#logoToggle"),
		logoHint: q("#logoHint"),
		share: q("#shareBtn"),
		downloadPng: q("#downloadPng"),
		downloadSvg: q("#downloadSvg"),
		copyPng: q("#copyPng"),
	};

	let renderToken = 0;
	let debounceTimer: ReturnType<typeof setTimeout> | undefined;
	/** The last successfully rendered input; downloads and copy use this. */
	let rendered: Rendered | null = null;

	// ---- reading form state -------------------------------------------------

	function readRadio<T extends string>(name: string, allowed: readonly T[], fallback: T): T {
		const checked = root.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`);
		return checked && (allowed as readonly string[]).includes(checked.value) ? (checked.value as T) : fallback;
	}

	function readEcc(): Ecc {
		const checked = root.querySelector<HTMLInputElement>('input[name="ecc"]:checked');
		return checked && isEcc(checked.value) ? checked.value : "M";
	}

	function readStyle(): QrStyle {
		return {
			shape: readRadio<ShapeStyle>("shape", SHAPE_STYLES, DEFAULT_STYLE.shape),
			color: readRadio<ColorStyle>("color", COLOR_STYLES, DEFAULT_STYLE.color),
			logo: refs.logoToggle.checked,
		};
	}

	function readText(): { text: string; isDefault: boolean } {
		const raw = refs.data.value.trim();
		return raw ? { text: raw, isDefault: false } : { text: DEFAULT_TEXT, isDefault: true };
	}

	function readSize(): number {
		return clampSize(refs.size.value, SIZE.min, SIZE.max, SIZE.fallback);
	}

	// ---- writing form state -------------------------------------------------

	function setRadio(name: string, value: string): void {
		const input = root.querySelector<HTMLInputElement>(`input[name="${name}"][value="${value}"]`);
		if (input) input.checked = true;
	}

	function applyStyle(style: QrStyle): void {
		setRadio("shape", style.shape);
		setRadio("color", style.color);
		refs.logoToggle.checked = style.logo;
	}

	/** The level the user had chosen before the logo pinned it to H, restored when the logo goes off. */
	let eccBeforeLogo: Ecc | null = null;

	/** With a logo the level is pinned to H; lock the other radios so the UI cannot disagree. */
	function syncEccLock(): void {
		const locked = refs.logoToggle.checked;
		const eccInputs = root.querySelectorAll<HTMLInputElement>('input[name="ecc"]');
		if (locked) {
			if (eccBeforeLogo === null) eccBeforeLogo = readEcc();
			eccInputs.forEach((input) => {
				const isLogoLevel = input.value === LOGO_ECC;
				input.disabled = !isLogoLevel;
				if (isLogoLevel) input.checked = true;
			});
		} else {
			eccInputs.forEach((input) => {
				input.disabled = false;
			});
			if (eccBeforeLogo !== null) {
				setRadio("ecc", eccBeforeLogo);
				eccBeforeLogo = null;
			}
		}
		refs.logoHint.hidden = !locked;
	}

	function persistStyle(style: QrStyle): void {
		try {
			localStorage.setItem(STYLE_STORAGE_KEY, JSON.stringify(style));
		} catch {
			// Storage unavailable (private mode, blocked); the choice simply isn't remembered.
		}
	}

	function loadStyle(): QrStyle {
		try {
			return parseStyle(localStorage.getItem(STYLE_STORAGE_KEY));
		} catch {
			return parseStyle(null);
		}
	}

	function setError(message: string | null): void {
		refs.errorBox.hidden = message === null;
		refs.errorBox.textContent = message ?? "";
	}

	function setActionsEnabled(enabled: boolean): void {
		for (const button of [refs.share, refs.downloadPng, refs.downloadSvg, refs.copyPng]) {
			button.disabled = !enabled;
		}
	}

	function updateCounter(): void {
		const { text, isDefault } = readText();
		const level = eccLevel(effectiveEcc(readEcc(), readStyle()));
		const bytes = isDefault ? 0 : utf8ByteLength(text);
		const over = bytes > level.byteCapacity;
		refs.byteCount.textContent = `${bytes} ${bytes === 1 ? "byte" : "bytes"}`;
		refs.byteCount.classList.toggle("text-danger", over);
		refs.byteCount.classList.toggle("fw-semibold", over);
		refs.byteLimit.textContent = `Byte-mode limit at ${level.id}: ${level.byteCapacity}`;
	}

	// ---- rendering ------------------------------------------------------------

	async function render(): Promise<void> {
		const token = ++renderToken;
		const { text, isDefault } = readText();
		const style = readStyle();
		const ecc = effectiveEcc(readEcc(), style);
		updateCounter();
		persistStyle(style);

		try {
			const info = describeSymbol(text, ecc); // throws if the data does not fit
			const img = await previewImage(text, ecc, PREVIEW_PX, style, "QR code preview"); // may tint the logo
			if (token !== renderToken) {
				URL.revokeObjectURL(img.src); // a newer render superseded this one
				return;
			}
			attachPreview(refs.preview, img);
			rendered = { text, ecc, style };
			setError(null);
			setActionsEnabled(true);
			const prefix = isDefault ? "Example. " : "";
			const eccNote = style.logo ? `ECC ${ecc} (logo)` : `ECC ${ecc}`;
			refs.status.textContent =
				`${prefix}Version ${info.version}, ${info.modules}×${info.modules} modules, ` +
				`${utf8ByteLength(text)} bytes, ${eccNote}. Data: ${text}`;
		} catch (err) {
			if (token !== renderToken) return;
			rendered = null;
			refs.preview.replaceChildren();
			setActionsEnabled(false);
			refs.status.textContent = "";
			setError(friendlyError(err));
		}
	}

	function scheduleRender(): void {
		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(() => void render(), DEBOUNCE_MS);
	}

	function triggerDownload(blob: Blob, filename: string): void {
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	}

	async function withBusy(button: HTMLButtonElement, work: () => Promise<void>): Promise<void> {
		const label = button.textContent;
		button.disabled = true;
		try {
			await work();
		} catch (err) {
			setError(friendlyError(err));
		} finally {
			button.disabled = false;
			button.textContent = label;
		}
	}

	// ---- events ---------------------------------------------------------------

	refs.data.addEventListener("input", scheduleRender);
	root.querySelectorAll<HTMLInputElement>('input[name="ecc"], input[name="shape"], input[name="color"]').forEach((input) => {
		input.addEventListener("change", () => void render());
	});
	refs.logoToggle.addEventListener("change", () => {
		syncEccLock();
		void render();
	});
	refs.form.addEventListener("submit", (e) => {
		e.preventDefault();
		clearTimeout(debounceTimer);
		void render();
	});
	refs.data.addEventListener("keydown", (e) => {
		if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
			e.preventDefault();
			refs.form.requestSubmit();
		}
	});
	refs.size.addEventListener("change", () => {
		refs.size.value = String(readSize());
	});

	// Share sheet (Save to Photos, Mail, Messages…) on phones and tablets that can share files.
	// Desktop browsers keep the plain download buttons even when they technically support sharing.
	if (canShareFiles() && isTouchDevice()) {
		refs.share.hidden = false;
		refs.share.classList.replace("btn-outline-primary", "btn-primary");
		refs.downloadPng.classList.replace("btn-primary", "btn-outline-primary");
		refs.share.addEventListener("click", () =>
			withBusy(refs.share, async () => {
				if (!rendered) return;
				const blob = await exportBlob(rendered.text, rendered.ecc, readSize(), rendered.style, "png");
				const name = `qrcode-${safeFileStem(rendered.text)}.png`;
				const outcome = await sharePng(blob, name, "QR code", rendered.text);
				if (outcome === "unsupported") triggerDownload(blob, name);
			}),
		);
	}

	refs.downloadPng.addEventListener("click", () =>
		withBusy(refs.downloadPng, async () => {
			if (!rendered) return;
			const blob = await exportBlob(rendered.text, rendered.ecc, readSize(), rendered.style, "png");
			triggerDownload(blob, `qrcode-${safeFileStem(rendered.text)}.png`);
		}),
	);

	refs.downloadSvg.addEventListener("click", () =>
		withBusy(refs.downloadSvg, async () => {
			if (!rendered) return;
			const blob = await exportBlob(rendered.text, rendered.ecc, readSize(), rendered.style, "svg");
			triggerDownload(blob, `qrcode-${safeFileStem(rendered.text)}.svg`);
		}),
	);

	const canCopyImages = typeof ClipboardItem !== "undefined" && !!navigator.clipboard?.write;
	if (canCopyImages) {
		refs.copyPng.addEventListener("click", () =>
			withBusy(refs.copyPng, async () => {
				if (!rendered) return;
				const blob = await exportBlob(rendered.text, rendered.ecc, readSize(), rendered.style, "png");
				await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
				refs.copyPng.textContent = "Copied";
				await new Promise((r) => setTimeout(r, 1200));
			}),
		);
	} else {
		refs.copyPng.hidden = true;
	}

	// ---- start ----------------------------------------------------------------

	// /?data=<text> prefills the input (used by "Customize style" on the files page).
	const preset = new URLSearchParams(location.search).get("data");
	if (preset && !refs.data.value) refs.data.value = preset;

	applyStyle(loadStyle());
	syncEccLock();
	void render();
}
